<template>
  <section class="document-library">
    <header class="library-header">
      <span class="header-icon"><v-icon>{{ area === "leadership" ? "mdi-folder-key-outline" : "mdi-bookshelf" }}</v-icon></span>
      <div>
        <div class="eyebrow">Aktuelle Unterlagen</div>
        <h2>{{ pageTitle }}</h2>
        <p>{{ pageDescription }}</p>
      </div>
      <v-text-field
        v-model="search"
        label="Dokument suchen"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        hide-details
        clearable
        class="search-field" />
    </header>

    <div v-if="loading" class="document-grid">
      <v-skeleton-loader v-for="index in 4" :key="index" dark type="article, actions" class="document-card" />
    </div>
    <div v-else-if="filteredDocuments.length" class="category-list">
      <section v-for="group in groupedDocuments" :key="group.category" class="category-section">
        <div class="category-heading">
          <div>
            <span>{{ group.category }}</span>
            <small>{{ group.items.length }} {{ group.items.length === 1 ? "Dokument" : "Dokumente" }}</small>
          </div>
        </div>
        <div class="document-grid">
          <article v-for="document in group.items" :key="document._id" class="document-card">
            <div class="document-top">
              <span><v-icon>mdi-file-pdf-box</v-icon></span>
              <div>
                <h3>{{ document.title }}</h3>
                <small>Version {{ document.version }} · {{ formatDate(document.updatedAt) }}</small>
              </div>
            </div>
            <p>{{ document.description || "Aktuelle Unterlage der well come FIT Akademie." }}</p>
            <div class="document-actions">
              <v-btn text color="#58d388" @click="openDocument(document)"><v-icon left>mdi-eye-outline</v-icon>Ansehen</v-btn>
              <v-btn text color="#d4d4d8" :loading="downloadingId === document._id" @click="downloadDocument(document)">
                <v-icon left>mdi-download-outline</v-icon>Download
              </v-btn>
            </div>
          </article>
        </div>
      </section>
    </div>
    <div v-else class="empty-state">
      <span><v-icon>mdi-folder-open-outline</v-icon></span>
      <h3>{{ search ? "Keine passenden Dokumente" : "Noch keine Dokumente verfügbar" }}</h3>
      <p>{{ search ? "Versuche einen anderen Suchbegriff." : "Neue und aktualisierte Unterlagen erscheinen automatisch hier." }}</p>
    </div>

    <v-dialog v-model="preview.show" max-width="62rem" dark scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card v-if="preview.document" class="preview-card">
        <header class="preview-header">
          <span><v-icon>mdi-file-pdf-box</v-icon></span>
          <div>
            <div class="eyebrow">{{ preview.document.category }}</div>
            <h2>{{ preview.document.title }}</h2>
            <p>Version {{ preview.document.version }} · {{ formatDate(preview.document.updatedAt) }}</p>
          </div>
          <v-btn icon aria-label="Dokument herunterladen" :loading="downloadingId === preview.document._id" @click="downloadDocument(preview.document)">
            <v-icon>mdi-download-outline</v-icon>
          </v-btn>
          <v-btn icon aria-label="Vorschau schliessen" @click="preview.show = false"><v-icon>mdi-close</v-icon></v-btn>
        </header>
        <v-card-text class="preview-body">
          <SecuredMedia
            :src="documentPath(preview.document)"
            media-type="pdf"
            :download-name="preview.document.currentFile && preview.document.currentFile.originalName"
            eager
            :cache="false" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3500" top right>{{ snackbar.text }}</v-snackbar>
  </section>
</template>

<script>
import api from "@/api"
import SecuredMedia from "./SecuredMedia.vue"

export default {
  name: "DocumentLibrary",
  components: { SecuredMedia },
  props: {
    area: { type: String, default: "general" },
  },
  data() {
    return {
      documents: [],
      loading: false,
      search: "",
      downloadingId: "",
      preview: { show: false, document: null },
      snackbar: { show: false, color: "success", text: "" },
    }
  },
  computed: {
    pageTitle() {
      return this.area === "leadership" ? "Leadership Docs" : "Guidelines"
    },
    pageDescription() {
      return this.area === "leadership"
        ? "Verbindliche Leitfäden und aktuelle Dokumente für Führungskräfte an einem geschützten Ort."
        : "Finde die jeweils aktuellen Leitfäden und Dokumente der Akademie zentral an einem Ort."
    },
    filteredDocuments() {
      const term = String(this.search || "").trim().toLowerCase()
      if (!term) return this.documents
      return this.documents.filter((document) => [document.title, document.description, document.category].some((value) => String(value || "").toLowerCase().includes(term)))
    },
    groupedDocuments() {
      const groups = new Map()
      this.filteredDocuments.forEach((document) => {
        const category = document.category || "Allgemein"
        if (!groups.has(category)) groups.set(category, [])
        groups.get(category).push(document)
      })
      return [...groups.entries()]
        .sort(([left], [right]) => left.localeCompare(right, "de"))
        .map(([category, items]) => ({ category, items: items.sort((left, right) => left.title.localeCompare(right.title, "de")) }))
    },
  },
  mounted() {
    this.loadDocuments()
  },
  watch: {
    area() {
      this.loadDocuments()
    },
  },
  methods: {
    async loadDocuments() {
      this.loading = true
      try {
        this.documents = (await api.akademie.fetchAcademyDocuments(this.area)) || []
      } catch (error) {
        this.documents = []
        this.snackbar = { show: true, color: "error", text: "Die Dokumente konnten nicht geladen werden." }
      } finally {
        this.loading = false
      }
    },
    openDocument(document) {
      this.preview = { show: true, document }
    },
    documentPath(document) {
      return api.akademie.getAcademyDocumentPath(document._id)
    },
    async downloadDocument(document) {
      this.downloadingId = document._id
      try {
        await api.akademie.downloadAcademyDocument(
          document._id,
          (document.currentFile && document.currentFile.originalName) || `${document.title}.pdf`,
        )
      } catch (error) {
        this.snackbar = { show: true, color: "error", text: "Das Dokument konnte nicht geöffnet werden." }
      } finally {
        this.downloadingId = ""
      }
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-"
    },
  },
}
</script>

<style scoped>
.document-library {
  color: #f4f4f5;
  font-size: 1rem;
}

.library-header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) minmax(15rem, 22rem);
  align-items: center;
  gap: 0.875rem;
  padding: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.25rem;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.08), transparent 55%), rgba(255, 255, 255, 0.03);
}

.header-icon,
.empty-state > span,
.preview-header > span,
.document-top > span {
  display: grid;
  place-items: center;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.header-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 0.875rem;
}

.header-icon .v-icon,
.empty-state > span .v-icon,
.preview-header > span .v-icon,
.document-top > span .v-icon {
  color: currentColor;
}

.eyebrow {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.library-header h2,
.preview-header h2 {
  margin: 0.1875rem 0 0.25rem;
  color: #fff;
  font-size: 1.5rem;
  line-height: 1.25;
}

.library-header p,
.preview-header p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.search-field ::v-deep .v-input__slot {
  border-radius: 0.8125rem !important;
  background: rgba(255, 255, 255, 0.045) !important;
}

.search-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.16) !important;
}

.search-field ::v-deep .v-label,
.search-field ::v-deep input {
  color: #f4f4f5 !important;
  font-size: 1rem;
}

.search-field ::v-deep input::placeholder {
  color: #a3a3a8 !important;
  opacity: 1;
}

.search-field ::v-deep .v-input__prepend-inner .v-icon,
.search-field ::v-deep .v-input__append-inner .v-icon {
  color: #a3a3a8 !important;
}

.category-list {
  display: grid;
  gap: 1.25rem;
  margin-top: 1rem;
}

.category-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.625rem;
}

.category-heading > div {
  display: flex;
  align-items: baseline;
  gap: 0.625rem;
}

.category-heading span {
  color: #fff;
  font-size: 1rem;
  font-weight: 750;
}

.category-heading small {
  color: #8f9095;
  font-size: 0.75rem;
}

.document-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.document-card {
  display: flex;
  min-height: 13rem;
  flex-direction: column;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.03);
}

.document-top {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
}

.document-top > span,
.preview-header > span {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.8125rem;
}

.document-top h3 {
  margin: 0;
  color: #fff;
  font-size: 1rem;
  line-height: 1.35;
}

.document-top small {
  color: #8f9095;
  font-size: 0.75rem;
}

.document-card > p {
  flex: 1 1 auto;
  margin: 0.875rem 0;
  color: #b8b8bc;
  font-size: 0.875rem;
  line-height: 1.5;
}

.document-actions {
  display: flex;
  gap: 0.25rem;
  padding-top: 0.625rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.document-actions .v-btn {
  font-size: 0.875rem;
  text-transform: none;
}

.empty-state {
  display: grid;
  min-height: 18rem;
  place-items: center;
  align-content: center;
  margin-top: 1rem;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.125rem;
  background: rgba(255, 255, 255, 0.025);
  text-align: center;
}

.empty-state > span {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 1rem;
}

.empty-state h3 {
  margin: 0.875rem 0 0.25rem;
  color: #fff;
  font-size: 1.125rem;
}

.empty-state p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
}

.preview-card {
  color: #f4f4f5;
  background: #191919 !important;
  font-size: 1rem;
}

.preview-header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.preview-header h2 {
  font-size: 1.125rem;
}

.preview-body {
  min-height: 32rem;
  padding: 1rem !important;
}

@media (max-width: 65rem) {
  .document-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .library-header {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .search-field {
    grid-column: 1 / -1;
  }
}

@media (max-width: 43rem) {
  .library-header {
    padding: 1rem;
  }

  .document-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .preview-header {
    grid-template-columns: auto minmax(0, 1fr) auto;
  }

  .preview-header > .v-btn:first-of-type {
    display: none;
  }
}
</style>
